import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AddUserDetails } from '../types/addUserType';
import { editAdmin } from '../api/editAdminApiService';
import { viewAdmin } from '../api/viewAdminApiService';
import { getAdminPrivilegesApiService } from '../api/getAdminPrivilegesApiService';
import { userStatusUpdateApiService } from '../api/userStatusUpdateApiService';
import { ADMIN_ERROR_MESSAGES } from '../errors';
import { translateRole } from '@/shared/utils/roleTranslation';
import {
  type AdminPrivileges,
  initialAdminPrivileges,
} from '../types/adminPrivilegeTypes';

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  REQUIRED: ADMIN_ERROR_MESSAGES.REQUIRED,
  EMAIL_INVALID_FORMAT: ADMIN_ERROR_MESSAGES.EMAIL_INVALID_FORMAT,
  PHONE_MIN_LENGTH: ADMIN_ERROR_MESSAGES.PHONE_MIN_LENGTH,
  ZIP_INVALID_FORMAT: ADMIN_ERROR_MESSAGES.ZIP_INVALID_FORMAT,
  EMAIL_ALREADY_EXISTS: ADMIN_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS,
  PRIVILEGE_REQUIRED: ADMIN_ERROR_MESSAGES.PRIVILEGE_REQUIRED,
} as const;

// ============================================================================
// Validation Functions
// ============================================================================

const validateName = (name: string): string => {
  if (!name.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  return '';
};

const validateEmail = (email: string): string => {
  if (!email.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return ERROR_MESSAGES.EMAIL_INVALID_FORMAT;
  }
  return '';
};

const validatePhone = (phone: string): string => {
  if (!phone.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  const digitsOnly = phone.replace(/\D/g, '');
  if (digitsOnly.length < 9) {
    return ERROR_MESSAGES.PHONE_MIN_LENGTH;
  }
  return '';
};

const validateAddress = (address: string): string => {
  if (!address.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  return '';
};

const validateCity = (city: string): string => {
  if (!city.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  return '';
};

const validateZip = (zip: string): string => {
  // Polish zip code format: XX-XXX (5 digits)
  const zipRegex = /^\d{2}-\d{3}$/;
  if (!zipRegex.test(zip.trim()) && zip.trim().length) {
    return ERROR_MESSAGES.ZIP_INVALID_FORMAT;
  }
  return '';
};

const validateState = (state: string): string => {
  if (!state.trim()) {
    return ERROR_MESSAGES.REQUIRED;
  }
  return '';
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extracts phone number without country code prefix
 */
const extractPhoneNumber = (phoneNumber: string | null): string => {
  if (!phoneNumber) {
    return '';
  }
  // Remove + prefix and country code (e.g., +48123456789 -> 123456789)
  return phoneNumber.replace(/^\+\d{1,2}/, '').trim();
};

/**
 * Maps ViewAdminUser data to AddUserDetails format
 */
const mapViewAdminUserToFormData = (user: {
  name: string;
  email: string;
  phoneNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  role: string;
  status: 'active' | 'inactive';
}): AddUserDetails => {
  return {
    name: user.name || '',
    email: user.email || '',
    phone: extractPhoneNumber(user.phoneNumber),
    address1: user.addressLine1 || '',
    address2: user.addressLine2 || '',
    city: user.city || '',
    state: user.state || '',
    zip: user.zipCode || '',
    country: user.country || 'Polska',
    role: translateRole(user.role),
    status: user.status || 'active',
  };
};

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseEditAdminReturn {
  // Form data
  formData: AddUserDetails;
  privileges: AdminPrivileges;

  // Errors
  errors: AddUserDetails;
  privilegesError: string;
  accessError: string | null;

  // Loading state
  isLoading: boolean;

  // Submission state
  isSubmitting: boolean;

  // Snackbar state
  snackbarError: string;
  isSnackbarOpen: boolean;
  handleCloseSnackbar: () => void;

  // Modal state
  isCancelModalOpen: boolean;
  handleOpenCancelModal: () => void;
  handleCloseCancelModal: () => void;
  handleConfirmCancel: () => void;

  // Field change handlers
  handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddress1Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddress2Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleStateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleZipChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePrivilegesChange: (p: AdminPrivileges) => void;

  // Form handlers
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;

  // Status handler
  status: 'active' | 'inactive';
  handleStatusChange: () => void;

  // Status dialog state
  isStatusDialogOpen: boolean;
  handleOpenStatusDialog: () => void;
  handleCloseStatusDialog: () => void;
  handleConfirmStatusUpdate: () => void;
  isStatusSubmitting: boolean;

  // Status update snackbar state
  showStatusSuccessSnackbar: boolean;
  statusSuccessMessage: string;
  handleCloseStatusSuccessSnackbar: () => void;
  showStatusErrorSnackbar: boolean;
  statusErrorMessage: string;
  handleCloseStatusErrorSnackbar: () => void;

  // Display-only fields
  adminId: string;
  createdAt: string;

  // Form change detection
  hasChanges: boolean;
}

// ============================================================================
// Hook
// ============================================================================

const initialFormData: AddUserDetails = {
  name: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'Polska',
  role: 'Administrator',
  status: 'active',
};

const initialErrors: AddUserDetails = {
  name: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  role: '',
};

export const useEditAdmin = (): UseEditAdminReturn => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  // State
  const [formData, setFormData] = useState<AddUserDetails>(initialFormData);
  const [originalFormData, setOriginalFormData] =
    useState<AddUserDetails>(initialFormData);
  const [errors, setErrors] = useState<AddUserDetails>(initialErrors);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [snackbarError, setSnackbarError] = useState<string>('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [adminId, setAdminId] = useState<string>('');
  const [createdAt, setCreatedAt] = useState<string>('');

  // Privileges state
  const [privileges, setPrivileges] = useState<AdminPrivileges>(
    initialAdminPrivileges
  );
  const [originalPrivileges, setOriginalPrivileges] = useState<AdminPrivileges>(
    initialAdminPrivileges
  );
  const [privilegesError, setPrivilegesError] = useState<string>('');
  const [accessError, setAccessError] = useState<string | null>(null);

  // Status dialog state
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState<boolean>(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState<boolean>(false);

  // Status update snackbar state
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState<boolean>(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState<string>('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] =
    useState<boolean>(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState<string>('');

  // Load user data on mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!userId) {
        setSnackbarError(ADMIN_ERROR_MESSAGES.DEFAULT_ERROR);
        setIsSnackbarOpen(true);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const [result, loadedPrivileges, session] = await Promise.all([
          viewAdmin({ userId, skipGlobalErrorRedirect: true }),
          getAdminPrivilegesApiService(userId),
          fetchAuthSession(),
        ]);

        if (result.success && result.data?.user) {
          const currentRole = session.tokens?.idToken?.payload?.[
            'custom:role'
          ] as string | undefined;

          // Check privilege for ADMIN users
          if (currentRole === 'ADMIN') {
            // If privileges failed to load (null), treat as no privileges
            if (!loadedPrivileges || !loadedPrivileges.adminUserEdit) {
              setAccessError('You do not have permission to edit admin users');
              setIsLoading(false);
              return;
            }
          }

          setAccessError(null);
          const mappedData = mapViewAdminUserToFormData(result.data.user);
          setFormData(mappedData);
          setOriginalFormData(mappedData);
          setAdminId(result.data.user.userId || '');
          if (loadedPrivileges) {
            setPrivileges(loadedPrivileges);
            setOriginalPrivileges(loadedPrivileges);
          }

          // Format createdAt date
          if (result.data.user.createdAt) {
            const date = new Date(result.data.user.createdAt);
            const formatter = new Intl.DateTimeFormat('pl-PL', {
              timeZone: 'Europe/Warsaw',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false,
            });
            const parts = formatter.formatToParts(date);
            const year = parts.find((p) => p.type === 'year')?.value;
            const month = parts.find((p) => p.type === 'month')?.value;
            const day = parts.find((p) => p.type === 'day')?.value;
            const hour = parts.find((p) => p.type === 'hour')?.value;
            const minute = parts.find((p) => p.type === 'minute')?.value;
            const second = parts.find((p) => p.type === 'second')?.value;
            setCreatedAt(
              `${year}/${month}/${day}, ${hour}:${minute}:${second}`
            );
          }
        } else {
          // If viewAdmin returned a 403, treat as access denied
          if (result.status === 403) {
            setAccessError('You do not have permission to edit admin users');
          } else {
            setSnackbarError(
              result.error || ADMIN_ERROR_MESSAGES.DEFAULT_ERROR
            );
            setIsSnackbarOpen(true);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setSnackbarError(ADMIN_ERROR_MESSAGES.UNEXPECTED_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [userId, navigate]);

  // Field change handlers
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, name: value }));
      if (errors.name) {
        setErrors((prev) => ({ ...prev, name: '' }));
      }
    },
    [errors.name]
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, email: value }));
      if (errors.email) {
        setErrors((prev) => ({ ...prev, email: '' }));
      }
    },
    [errors.email]
  );

  const PHONE_MAX_LENGTH = 9;

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim().slice(0, PHONE_MAX_LENGTH);
      setFormData((prev) => ({ ...prev, phone: value }));
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: '' }));
      }
    },
    [errors.phone]
  );

  const handleAddress1Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, address1: value }));
      if (errors.address1) {
        setErrors((prev) => ({ ...prev, address1: '' }));
      }
    },
    [errors.address1]
  );

  const handleAddress2Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, address2: value }));
    },
    []
  );

  const handleCityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, city: value }));
      if (errors.city) {
        setErrors((prev) => ({ ...prev, city: '' }));
      }
    },
    [errors.city]
  );

  const handleStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, state: value }));
      if (errors.state) {
        setErrors((prev) => ({ ...prev, state: '' }));
      }
    },
    [errors.state]
  );

  const handleZipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, zip: value }));
      if (errors.zip) {
        setErrors((prev) => ({ ...prev, zip: '' }));
      }
    },
    [errors.zip]
  );

  const handlePrivilegesChange = useCallback(
    (p: AdminPrivileges) => {
      setPrivileges(p);
      if (privilegesError) {
        setPrivilegesError('');
      }
    },
    [privilegesError]
  );

  // Form submission handler
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!userId) {
        setSnackbarError(ADMIN_ERROR_MESSAGES.DEFAULT_ERROR);
        setIsSnackbarOpen(true);
        return;
      }

      // Validate all fields
      const validationErrors: AddUserDetails = {
        name: validateName(formData.name),
        email: validateEmail(formData.email),
        phone: validatePhone(formData.phone),
        address1: validateAddress(formData.address1),
        address2: '', // Address2 is optional
        city: validateCity(formData.city),
        state: validateState(formData.state),
        zip: validateZip(formData.zip),
        country: '', // Country is disabled/pre-filled
        role: '', // Role is disabled/pre-filled
      };

      // Set all errors at once
      setErrors(validationErrors);

      // Check if there are any errors
      const hasErrors = Object.values(validationErrors).some(
        (error) => error !== ''
      );
      const hasAtLeastOnePrivilege = Object.values(privileges).some(Boolean);
      if (!hasAtLeastOnePrivilege) {
        setPrivilegesError(ERROR_MESSAGES.PRIVILEGE_REQUIRED);
      }

      if (!hasErrors && hasAtLeastOnePrivilege) {
        // Handle form submission - all validations passed
        setIsSubmitting(true);
        try {
          const result = await editAdmin(userId, formData, { privileges });
          if (result.success) {
            // Navigate to administrators list with success state
            navigate('/administrators', {
              replace: true,
              state: { showEditAdminSuccess: true },
            });
          } else {
            // Handle error
            if (result.error === ERROR_MESSAGES.EMAIL_ALREADY_EXISTS) {
              // Show email error inline
              setErrors((prev) => ({
                ...prev,
                email: result.error || '',
              }));
            } else {
              // Show other errors in snackbar
              setSnackbarError(
                result.error || ADMIN_ERROR_MESSAGES.DEFAULT_ERROR
              );
              setIsSnackbarOpen(true);
            }
            console.error('Error:', result.error);
            setIsSubmitting(false);
          }
        } catch (error) {
          console.error('Unexpected error:', error);
          // Show unexpected error in snackbar
          setSnackbarError(ADMIN_ERROR_MESSAGES.UNEXPECTED_ERROR);
          setIsSnackbarOpen(true);
          setIsSubmitting(false);
        }
      }
    },
    [formData, userId, navigate, privileges]
  );

  // Check if form has changes compared to original data.
  // Status is intentionally excluded — the status toggle has its own
  // confirmation dialog and API call, so it's independent of the form's
  // save/cancel lifecycle.
  const hasChanges = useMemo(() => {
    const profileChanged =
      formData.name.trim() !== originalFormData.name.trim() ||
      formData.email.trim() !== originalFormData.email.trim() ||
      formData.phone.trim() !== originalFormData.phone.trim() ||
      formData.address1.trim() !== originalFormData.address1.trim() ||
      formData.address2.trim() !== originalFormData.address2.trim() ||
      formData.city.trim() !== originalFormData.city.trim() ||
      formData.state.trim() !== originalFormData.state.trim() ||
      formData.zip.trim() !== originalFormData.zip.trim();

    const privilegesChanged = (
      Object.keys(privileges) as Array<keyof AdminPrivileges>
    ).some((k) => privileges[k] !== originalPrivileges[k]);

    return profileChanged || privilegesChanged;
  }, [formData, originalFormData, privileges, originalPrivileges]);

  const handleCancel = useCallback(() => {
    if (!hasChanges) {
      navigate('/administrators', { replace: true });
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasChanges, navigate]);

  // Modal handlers
  const handleOpenCancelModal = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  // Confirm cancel - actually cancels and navigates away
  const handleConfirmCancel = useCallback(() => {
    // Reset form to initial state
    setFormData(initialFormData);
    setErrors(initialErrors);
    setIsSnackbarOpen(false);
    setSnackbarError('');
    setIsCancelModalOpen(false);
    // Navigate back to administrators list
    navigate('/administrators', { replace: true });
  }, [navigate]);

  // Snackbar close handler
  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  // Status change handler - opens dialog instead of directly changing status
  const handleStatusChange = useCallback(() => {
    setIsStatusDialogOpen(true);
  }, []);

  // Status dialog handlers
  const handleOpenStatusDialog = useCallback(() => {
    setIsStatusDialogOpen(true);
  }, []);

  const handleCloseStatusDialog = useCallback(() => {
    if (!isStatusSubmitting) {
      setIsStatusDialogOpen(false);
    }
  }, [isStatusSubmitting]);

  // Handle status update confirmation
  const handleConfirmStatusUpdate = useCallback(async () => {
    if (!userId) {
      setStatusErrorMessage(ADMIN_ERROR_MESSAGES.DEFAULT_ERROR);
      setShowStatusErrorSnackbar(true);
      return;
    }

    setIsStatusSubmitting(true);
    const isActive = formData.status === 'inactive'; // Will activate if currently inactive

    try {
      const result = await userStatusUpdateApiService.updateUserStatus({
        userId,
        isActive,
      });

      if (result.success) {
        // Update form data with new status
        const newStatus: 'active' | 'inactive' = isActive
          ? 'active'
          : 'inactive';
        setFormData((prev) => ({ ...prev, status: newStatus }));
        // Don't update originalFormData.status - status updates should be tracked as changes

        // Show success snackbar
        const message = isActive
          ? 'Administrator został pomyślnie aktywowany.'
          : 'Administrator został pomyślnie dezaktywowany.';
        setStatusSuccessMessage(message);
        setShowStatusSuccessSnackbar(true);
        setIsStatusDialogOpen(false);
      } else {
        // Show error snackbar
        setStatusErrorMessage(
          result.error || 'Wystąpił błąd podczas aktualizacji statusu.'
        );
        setShowStatusErrorSnackbar(true);
      }
    } catch (_error) {
      // Show error snackbar
      setStatusErrorMessage(
        'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.'
      );
      setShowStatusErrorSnackbar(true);
    } finally {
      setIsStatusSubmitting(false);
    }
  }, [userId, formData.status]);

  // Status snackbar handlers
  const handleCloseStatusSuccessSnackbar = useCallback(() => {
    setShowStatusSuccessSnackbar(false);
    setStatusSuccessMessage('');
  }, []);

  const handleCloseStatusErrorSnackbar = useCallback(() => {
    setShowStatusErrorSnackbar(false);
    setStatusErrorMessage('');
  }, []);

  return {
    formData,
    privileges,
    errors,
    privilegesError,
    accessError,
    isLoading,
    isSubmitting,
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    handleNameChange,
    handleEmailChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handlePrivilegesChange,
    handleSubmit,
    handleCancel,
    isCancelModalOpen,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleConfirmCancel,
    status: formData.status || 'active',
    handleStatusChange,
    isStatusDialogOpen,
    handleOpenStatusDialog,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    handleCloseStatusSuccessSnackbar,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleCloseStatusErrorSnackbar,
    adminId,
    createdAt,
    hasChanges,
  };
};
